import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowUpRight, PlayCircle, FileText, ExternalLink, GraduationCap, Sparkles,
} from "lucide-react";
import { RiskBanner, RiskFooter } from "@/components/RiskDisclaimer";
import { trainingApi } from "@/lib/api";

const TYPE_META = {
  video: { icon: PlayCircle, tone: "emerald", label: "Video" },
  document: { icon: FileText, tone: "amber", label: "Document" },
  link: { icon: ExternalLink, tone: "sky", label: "Guide" },
};

function ResourceTile({ r }) {
  const meta = TYPE_META[r.type] || TYPE_META.link;
  const Icon = meta.icon;
  const isInternal = r.url.startsWith("/") && r.type !== "document";
  const isVideoEmbed = r.type === "video" && (r.url.includes("youtube.com/embed") || r.url.includes("player.vimeo"));
  const isDirectVideo = r.type === "video" && /\.(mp4|webm|mov|m4v)(\?|$)/i.test(r.url);
  const isImageDoc = r.type === "document" && /\.(png|jpe?g|webp|gif)$/i.test(r.url);
  const previewImg = r.thumbnail || (isImageDoc ? r.url : null);

  const [expanded, setExpanded] = useState(false);

  const tileClass =
    "group rounded-2xl border border-white/10 bg-[#0F111A]/70 backdrop-blur-sm hover:border-emerald-400/40 transition overflow-hidden";
  const toneRing = {
    emerald: "text-emerald-300 bg-emerald-500/15 group-hover:bg-emerald-500/25",
    amber: "text-amber-300 bg-amber-500/15 group-hover:bg-amber-500/25",
    sky: "text-sky-300 bg-sky-500/15 group-hover:bg-sky-500/25",
  }[meta.tone];

  return (
    <div data-testid={`training-tile-${r.id}`} className={tileClass}>
      {/* Video: inline embed on expand; other types: link/download */}
      {isVideoEmbed && expanded ? (
        <div className="aspect-video bg-black">
          <iframe
            src={r.url}
            title={r.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      ) : isDirectVideo && expanded ? (
        <div className="aspect-video bg-black">
          <video
            src={r.url}
            title={r.title}
            controls
            autoPlay
            playsInline
            className="w-full h-full object-contain"
          />
        </div>
      ) : (
        <button
          data-testid={`training-tile-cta-${r.id}`}
          onClick={(e) => {
            if (isVideoEmbed || isDirectVideo) { e.preventDefault(); setExpanded(true); return; }
            if (isInternal) return; // let Link handle
            window.open(r.url, "_blank", "noopener,noreferrer");
          }}
          className="w-full text-left"
        >
          <div className="aspect-video bg-gradient-to-br from-[#0A0C15] via-[#0F111A] to-[#0A0C15] grid place-items-center relative overflow-hidden">
            {previewImg ? (
              <>
                <img
                  src={previewImg}
                  alt={r.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition"
                  loading="lazy"
                  draggable={false}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
              </>
            ) : (
              <span className={`grid place-items-center w-16 h-16 rounded-full ${toneRing} transition`}>
                <Icon className="w-8 h-8" />
              </span>
            )}
            <div className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-[0.25em] text-slate-200 bg-black/60 backdrop-blur px-2 py-1 rounded-full">
              {meta.label}
            </div>
          </div>
        </button>
      )}
      <div className="p-4">
        <div className="font-display font-bold text-slate-100 leading-snug">{r.title}</div>
        {r.description && (
          <p className="mt-1.5 text-[13px] text-slate-400 leading-relaxed">{r.description}</p>
        )}
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          {isInternal ? (
            <Link
              to={r.url}
              data-testid={`training-open-${r.id}`}
              className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-emerald-300 hover:text-emerald-200"
            >
              Open <ArrowUpRight className="w-3 h-3" />
            </Link>
          ) : (isVideoEmbed || isDirectVideo) && !expanded ? (
            <button
              data-testid={`training-open-${r.id}`}
              onClick={() => setExpanded(true)}
              className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-emerald-300 hover:text-emerald-200"
            >
              Play <PlayCircle className="w-3 h-3" />
            </button>
          ) : (
            <a
              href={r.type === "document" ? trainingApi.downloadUrl(r.id) : r.url}
              target={r.type === "document" ? "_self" : "_blank"}
              rel="noopener noreferrer"
              data-testid={`training-open-${r.id}`}
              download={r.type === "document" ? true : undefined}
              className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-emerald-300 hover:text-emerald-200"
            >
              {r.type === "document" ? "Download" : "Open"}
              <ArrowUpRight className="w-3 h-3" />
            </a>
          )}
          {isDirectVideo && (
            <a
              href={trainingApi.downloadUrl(r.id)}
              download
              data-testid={`training-download-${r.id}`}
              className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-slate-400 hover:text-emerald-300"
              title="Download this video"
            >
              Download <ArrowUpRight className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TrainingCenter() {
  const [resources, setResources] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    trainingApi.list()
      .then((r) => setResources(r.resources || []))
      .catch((e) => { console.debug("[Training] load failed", e); setErr("Failed to load training materials."); });
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0C12] text-slate-100 flex flex-col">
      <RiskBanner />
      <header className="max-w-5xl w-full mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" data-testid="training-back-home" className="flex items-center gap-3 group">
          <img
            src="/brand/tradingox-logo.png"
            alt="TradingoX"
            className="w-10 h-10 rounded-full ring-1 ring-white/10 shadow-[0_0_20px_rgba(16,185,129,0.25)]"
          />
          <div className="font-display text-lg font-bold tracking-tight">TradingoX</div>
        </Link>
        <Link
          to="/"
          data-testid="training-back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-100 transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back home
        </Link>
      </header>

      <main className="max-w-6xl w-full mx-auto px-6 pt-8 pb-16 flex-1">
        <div className="mb-10 text-center">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.35em] text-emerald-300 mb-2">
            <GraduationCap className="w-3.5 h-3.5" /> learn & level up
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            Training <span className="text-emerald-400">Center</span>
          </h1>
          <p className="mt-3 text-slate-400 text-base max-w-2xl mx-auto">
            Videos, guides, and reference documents to help you master TradingoX and trade with confidence.
          </p>
        </div>

        {err && (
          <div data-testid="training-error" className="mb-6 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">
            {err}
          </div>
        )}

        {resources === null && !err && (
          <div className="text-center py-16 text-slate-500 text-sm font-mono">Loading materials…</div>
        )}

        {resources && resources.length === 0 && !err && (
          <div className="text-center py-16 text-slate-400" data-testid="training-empty">
            <Sparkles className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
            <div className="font-display text-xl font-bold">Materials coming soon</div>
            <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
              We're recording the first batch of tutorials. Check back this week or ask Maiv anything in the meantime.
            </p>
          </div>
        )}

        {resources && resources.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="training-grid">
            {resources.map((r) => <ResourceTile key={r.id} r={r} />)}
          </div>
        )}

        <div className="mt-14 rounded-2xl border border-emerald-400/20 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent p-6 text-center">
          <div className="text-sm text-slate-400 mb-1">Have a topic you want covered?</div>
          <div className="font-display text-xl font-bold">
            Chat with <span className="text-emerald-300">Maiv</span>, or{" "}
            <Link to="/contact" className="text-emerald-300 underline underline-offset-2">send us a request</Link>.
          </div>
        </div>
      </main>

      <RiskFooter />
    </div>
  );
}
